import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, from, firstValueFrom } from "rxjs";
import { shareReplay } from "rxjs/operators";
import { HybridStorageKeys, HybridStorageService } from "../../services/hybrid-storage.service";
import { RuntimeConfigService } from "../../../services/runtime-config.service";

/** Shape of the CMS raw-content response for ?properties=SchoolMealsURL. */
interface SchoolMealsRawContent {
  SchoolMealsURL?: {
    Type: string;
    Value: string | null;
    FileUrl: string;
  };
}

/**
 * Looks up a school's lunch-menu URL from the public nhaschools.com CMS (Kentico) — the same
 * source the Scholar parent portal uses, so the two apps stay consistent. The URL is managed
 * by NHA content editors: never hardcode menu URLs or assume a vendor (most are Nutrislice,
 * but not all); a school with no URL set simply has no menu link.
 */
@Injectable({
  providedIn: 'root'
})
export class SchoolMealsService {
  // Menu URLs change at content-editor cadence — cache for hours, not seconds.
  private static readonly CACHE_MINUTES = 6 * 60;

  // One shared lookup per school per session; persisted lookups are served from hybrid storage.
  private menuUrlBySchoolEID = new Map<number, Observable<string | null>>();

  constructor(
    private http: HttpClient,
    private hybridStorageService: HybridStorageService,
    private runtimeConfig: RuntimeConfigService
  ) { }

  // Public, unauthenticated endpoint (CORS *), keyed by the NHA/SIS integer school ID
  // (School.SchoolEID here — NOT this app's local Core.Schools.Id). Unversioned internal CMS
  // plumbing, but Scholar's production API depends on it, so it won't change silently.
  private get cmsRawContentUrl(): string {
    return `${this.runtimeConfig.currentConfig.cmsBaseUrl}/api/schools/raw-content`;
  }

  /**
   * Emits the school's lunch-menu URL, or null when the school has none or the lookup fails —
   * callers hide the link either way (no disabled state, no placeholder). Treat the URL as
   * opaque and just link to it.
   */
  getMealsMenuUrl(schoolEID: number): Observable<string | null> {
    let menuUrl$ = this.menuUrlBySchoolEID.get(schoolEID);
    if (!menuUrl$) {
      menuUrl$ = from(this.loadMenuUrl(schoolEID)).pipe(shareReplay(1));
      this.menuUrlBySchoolEID.set(schoolEID, menuUrl$);
    }
    return menuUrl$;
  }

  private async loadMenuUrl(schoolEID: number): Promise<string | null> {
    const cacheKey = HybridStorageKeys.SchoolMeals_MenuUrlPrefix + schoolEID;

    // Stored as { url } so a cached "no menu" (null) is distinguishable from a cache miss.
    const stored = await this.hybridStorageService.getItemIfFresh<{ url: string | null }>(
      cacheKey, SchoolMealsService.CACHE_MINUTES);
    if (stored) {
      return stored.url;
    }

    try {
      // A school ID unknown to the CMS returns 204 No Content; HttpClient yields a null body.
      const response = await firstValueFrom(this.http.get<SchoolMealsRawContent | null>(
        `${this.cmsRawContentUrl}/${schoolEID}?properties=SchoolMealsURL`));
      const url = response?.SchoolMealsURL?.Value?.trim() || null;

      // Cache empty results too — "no menu" is a valid, stable answer for that school.
      await this.hybridStorageService.setItem(cacheKey, { url });
      return url;
    } catch (error) {
      // Fail soft: hide the link. Not cached, and the in-session share is dropped, so the
      // next main-menu visit retries.
      console.error('Error fetching lunch menu URL for school', schoolEID, error);
      this.menuUrlBySchoolEID.delete(schoolEID);
      return null;
    }
  }
}
